import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { EVERYONE_GROUP, APP_WIDE_GROUP } from '../constants';
import CourseAutocomplete from './CourseAutocomplete';

const TIP_STORAGE_KEY = 'greentime.seenEmailImportTip';

export default function PostTeeTimeModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ courseName:'', courseUrl:'', coursePhone:'', teeDate:'', teeTime:'08:00', totalSpots:4, holes:18, isPrivate:true });
  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [isEveryone, setIsEveryone] = useState(false);
  const [isAppWide, setIsAppWide] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setForm(f => ({ ...f, teeDate: today }));
    api.getGroups().then(setGroups).catch(() => {});
    // Show tip overlay only on first visit (per-browser)
    try {
      if (!localStorage.getItem(TIP_STORAGE_KEY)) setShowTip(true);
    } catch { /* localStorage unavailable — silently skip */ }
  }, []);

  const dismissTip = () => {
    try { localStorage.setItem(TIP_STORAGE_KEY, '1'); } catch { /* ignore */ }
    setShowTip(false);
  };

  const set = (k) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: val }));
  };

  const toggleGroup = (id) => {
    setSelectedGroupIds(prev =>
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await api.createTeeTime({
        courseName: form.courseName, courseUrl: form.courseUrl || undefined,
        coursePhone: form.coursePhone || undefined,
        teeDate: form.teeDate, teeTime: form.teeTime,
        totalSpots: parseInt(form.totalSpots), holes: parseInt(form.holes), isPrivate: form.isPrivate,
        isEveryone,
        isAppWide,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        groupIds: selectedGroupIds,
      });
      onCreated();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ position: 'relative' }}>
        <h3 style={{ marginBottom: 4 }}>Post a tee time</h3>
        <button
          type="button"
          onClick={() => setShowTip(true)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#2e7d4f', fontSize: 12, padding: 0, marginBottom: 12,
            textDecoration: 'underline', textUnderlineOffset: 2,
          }}
        >
          ℹ️ Save time with email import
        </button>
        <form onSubmit={submit}>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Date</label><input className="form-input" type="date" value={form.teeDate} onChange={set('teeDate')} required /></div>
            <div className="form-group"><label className="form-label">Tee time</label><input className="form-input" type="time" value={form.teeTime} onChange={set('teeTime')} required /></div>
          </div>
          <div className="form-group">
            <label className="form-label">Course name</label>
            <CourseAutocomplete
              value={form.courseName}
              onChange={(v) => setForm(f => ({ ...f, courseName: v }))}
              onSelect={({ courseName, courseUrl, coursePhone }) => setForm(f => ({
                ...f,
                courseName,
                courseUrl:   courseUrl   || f.courseUrl,
                coursePhone: coursePhone || f.coursePhone,
              }))}
              placeholder="e.g. Indian Canyon GC"
              required
            />
          </div>
          <div className="form-group"><label className="form-label">Course Tee Time Website (optional)</label><input className="form-input" type="url" value={form.courseUrl} onChange={set('courseUrl')} placeholder="https://..." /></div>
          <div className="form-group"><label className="form-label">Course phone (optional)</label><input className="form-input" type="tel" value={form.coursePhone} onChange={set('coursePhone')} placeholder="(555) 123-4567" /></div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Total spots</label>
              <select className="form-input" value={form.totalSpots} onChange={set('totalSpots')}>
                <option value={1}>1 golfer (just me)</option><option value={2}>2 golfers</option><option value={3}>3 golfers</option><option value={4}>4 golfers</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Holes</label>
              <select className="form-input" value={form.holes} onChange={set('holes')}>
                <option value={18}>18 holes</option><option value={9}>9 holes</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Post to groups (optional)</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
              {/* Everyone — always shown at top */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#2a2a1a', marginBottom: 2 }}>
                <input
                  type="checkbox"
                  checked={isEveryone}
                  onChange={() => setIsEveryone(v => !v)}
                  style={{ accentColor: EVERYONE_GROUP.color, width: 15, height: 15 }}
                />
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  background: isEveryone ? EVERYONE_GROUP.color : '#f4f1eb',
                  color: isEveryone ? '#e8f5ec' : '#2a2a1a',
                  borderRadius: 20, padding: '2px 10px', fontSize: 13, transition: 'all 0.15s',
                }}>
                  {EVERYONE_GROUP.emoji} {EVERYONE_GROUP.name}
                </span>
                <span style={{ fontSize: 11, color: '#7a7a6a' }}>(your friends only)</span>
              </label>
              {/* App-Wide — discovery to any Greentime user nearby; no notifications */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#2a2a1a', paddingBottom: 6, borderBottom: '1px solid #f0ede4', marginBottom: 2 }}>
                <input
                  type="checkbox"
                  checked={isAppWide}
                  onChange={() => setIsAppWide(v => !v)}
                  style={{ accentColor: APP_WIDE_GROUP.color, width: 15, height: 15 }}
                />
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  background: isAppWide ? APP_WIDE_GROUP.color : '#f4f1eb',
                  color: isAppWide ? '#faf6e8' : '#2a2a1a',
                  borderRadius: 20, padding: '2px 10px', fontSize: 13, transition: 'all 0.15s',
                }}>
                  {APP_WIDE_GROUP.emoji} {APP_WIDE_GROUP.name}
                </span>
                <span style={{ fontSize: 11, color: '#7a7a6a' }}>(anyone nearby — no notifications)</span>
              </label>
              {groups.length > 0 ? groups.map(g => (
                <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#2a2a1a' }}>
                  <input
                    type="checkbox"
                    checked={selectedGroupIds.includes(g.id)}
                    onChange={() => toggleGroup(g.id)}
                    style={{ accentColor: g.color || '#2e7d4f', width: 15, height: 15 }}
                  />
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    background: selectedGroupIds.includes(g.id) ? (g.color || '#0d2b1a') : '#f4f1eb',
                    color: selectedGroupIds.includes(g.id) ? '#e8f5ec' : '#2a2a1a',
                    borderRadius: 20, padding: '2px 10px', fontSize: 13, transition: 'all 0.15s',
                  }}>
                    {g.emoji} {g.name}
                  </span>
                </label>
              )) : (
                <p style={{ fontSize:12, color:'#9a9a8a', margin:0 }}>No custom groups yet — create one in <strong>My Groups</strong>.</p>
              )}
            </div>
          </div>
          {error && <p className="form-error">{error}</p>}
          <div className="modal-actions">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-accent btn-sm" disabled={loading}>{loading ? 'Posting…' : 'Post tee time'}</button>
          </div>
        </form>

        {showTip && (
          <div
            onClick={(e) => e.target === e.currentTarget && dismissTip()}
            style={{
              position: 'absolute', inset: 0, background: 'rgba(13, 43, 26, 0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '1.2rem', borderRadius: 'inherit', zIndex: 10,
            }}>
            <div style={{
              background: '#fff', borderRadius: 12, padding: '1.2rem 1.3rem',
              maxWidth: 380, boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
              border: '1.5px solid #c8e8d0',
            }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>💡</div>
              <div style={{ fontSize: 15, fontWeight: 500, color: '#0d2b1a', marginBottom: 8 }}>
                Tip: Save time on future tee times
              </div>
              <div style={{ fontSize: 13, color: '#4a4a3a', lineHeight: 1.5, marginBottom: 12 }}>
                Booking confirmation emails from your golf course can be forwarded directly to{' '}
                <span style={{ fontFamily: 'monospace', background: '#f4f1eb', padding: '1px 6px', borderRadius: 5, color: '#0d2b1a' }}>
                  add@mail.greentime.app
                </span>{' '}
                — we'll automatically import the date, time, course, and phone number for you.
              </div>
              <div style={{ fontSize: 12, color: '#7a7a6a', marginBottom: 14 }}>
                No need to manually fill out this form every time.
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-accent btn-sm"
                  onClick={dismissTip}
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
